import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import { MonsterThumb, monsterImgSrc } from '../ui/ZoneMonsterPicker';
import useZoneMonsters from '../../hooks/useZoneMonsters';
import { fmtDateTime, fmtKamas, fmtKamasLong, fmtRemaining, useServerClock } from './auctionUtils';
import AuctionResults from './AuctionResults';
import './Encheres.css';

// Fond flouté de la carte : l'image du monstre, en grand
const heroStyle = (monster) => (monster?.img ? { '--auc-img': `url("${monsterImgSrc(monster.img)}")` } : undefined);

const FILTERS = [
    { id: 'all', labelKey: 'auctionMember.filterAll', label: 'Toutes' },
    { id: 'mine', labelKey: 'auctionMember.filterMine', label: 'Mes mises' },
    { id: 'free', labelKey: 'auctionMember.filterFree', label: 'Sans mise' }
];

/* ── Une zone aux enchères ── */
const LotCard = ({ lot, auction, now, onBid, favorite, onToggleFavorite, monster }) => {
    const { t } = useLanguage();
    const [amount, setAmount] = useState('');
    const [sending, setSending] = useState(false);
    const remaining = new Date(lot.endsAt).getTime() - now;
    const ended = lot.ended || remaining <= 0;
    const snipe = !ended && remaining < auction.antiSnipeMinutes * 60000;
    const outbid = lot.hasBid && !lot.isLeader;

    // La saisie repart de la mise minimale quand la zone change
    useEffect(() => { setAmount(''); }, [lot.minBid]);

    const value = Number(amount);
    const tooLow = amount === '' || !Number.isFinite(value) || value < lot.minBid;
    // Surenchères rapides : +1, +2 ou +3 paliers au-dessus de la meilleure mise (ou depuis la mise de départ)
    const quickBids = [0, 1, 2].map(i => lot.minBid + i * auction.minIncrement);
    const quickLabel = (total) => (lot.amount > 0 ? `+${fmtKamas(total - lot.amount)}` : fmtKamas(total));

    const placeBid = async (total) => {
        if (!window.confirm(t('auctionMember.confirmBid', 'Miser {amount} sur « {zone} » ?\n\nUne mise ne peut pas être retirée.', { amount: fmtKamasLong(total), zone: lot.zone }))) return;
        setSending(true);
        await onBid(lot, total);
        setSending(false);
    };

    const submit = (e) => {
        e.preventDefault();
        if (!tooLow) placeBid(value);
    };

    return (
        <li className={`c2-tile auc-lot${lot.isLeader ? ' is-leader' : ''}${outbid ? ' is-outbid' : ''}${ended ? ' is-ended' : ''}${snipe ? ' is-snipe' : ''}${favorite ? ' is-favorite' : ''}${monster?.img ? ' has-img' : ''}`}>
            <div className="auc-lot__hero" style={heroStyle(monster)}>
                <MonsterThumb monster={monster} zone={lot.zone} size={84} className="auc-lot__monster" />
                <span className="auc-lot__name">
                    <strong>{lot.zone}</strong>
                    {lot.donjon && <span>{lot.donjon}</span>}
                    {monster?.name && monster.name.toLowerCase() !== lot.zone.toLowerCase() && <span className="auc-lot__boss">{monster.name}</span>}
                </span>
                <span className="auc-lot__badges">
                    {lot.isLeader && <span className="c2-chip c2-chip--green">{ended ? t('auctionMember.won', 'Remportée') : t('auctionMember.leading', 'En tête')}</span>}
                    {outbid && <span className="c2-chip auc-chip--outbid">{t('auctionMember.outbid', 'Dépassé')}</span>}
                    <button
                        type="button"
                        className={`auc-fav${favorite ? ' is-active' : ''}`}
                        onClick={() => onToggleFavorite(lot.zone)}
                        aria-pressed={favorite}
                        title={favorite ? t('auctionMember.unfavorite', 'Retirer des favoris') : t('auctionMember.favorite', 'Mettre en favori')}
                        aria-label={favorite ? t('auctionMember.unfavorite', 'Retirer des favoris') : t('auctionMember.favorite', 'Mettre en favori')}
                    >
                        <Icon name="star" size={16} filled={favorite} />
                    </button>
                </span>
            </div>

            <div className="auc-lot__body">
            <div className="auc-lot__price">
                {lot.amount > 0 ? (
                    <>
                        <strong>{fmtKamas(lot.amount)}</strong>
                        <span>{lot.bidderName} · {lot.bidCount > 1 ? t('auctionMember.bids', '{n} mises', { n: lot.bidCount }) : t('auctionMember.bid', '{n} mise', { n: lot.bidCount })}</span>
                    </>
                ) : (
                    <>
                        <strong className="c2-muted">—</strong>
                        <span>{t('auctionMember.startsAt', 'Départ à {amount}', { amount: fmtKamas(auction.startingBid) })}</span>
                    </>
                )}
            </div>

            <div className={`auc-lot__time${snipe ? ' auc-soon' : ''}`}>
                <Icon name="clock" size={14} />
                {ended ? (lot.amount > 0 ? t('auctionMember.wonBy', 'Remportée par {name}', { name: lot.bidderName }) : t('auctionMember.endedNoBid', 'Terminée sans mise')) : fmtRemaining(remaining)}
                {snipe && <span className="auc-lot__snipe">anti-snipe</span>}
            </div>

            {!ended && !lot.isLeader && (
                <div className="auc-lot__actions">
                    <div className="auc-lot__quick">
                        {quickBids.map(total => (
                            <button
                                key={total}
                                type="button"
                                className="auc-quick"
                                onClick={() => placeBid(total)}
                                disabled={sending}
                                title={t('auctionMember.bidAmount', 'Miser {amount}', { amount: fmtKamasLong(total) })}
                            >
                                {quickLabel(total)}
                            </button>
                        ))}
                    </div>
                    <form className="auc-lot__bid" onSubmit={submit}>
                        <input
                            type="number"
                            className="c2-input c2-input--sm"
                            min={lot.minBid}
                            step={auction.minIncrement}
                            placeholder={t('auctionMember.otherAmount', 'Autre montant (min. {min})', { min: lot.minBid.toLocaleString(getLocale()) })}
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            aria-label={t('auctionMember.otherAmountOn', 'Autre montant sur {zone}', { zone: lot.zone })}
                        />
                        <button type="submit" className="c2-btn c2-btn--ghost c2-btn--sm" disabled={sending || tooLow}>
                            {t('auctionMember.placeBid', 'Miser')}
                        </button>
                    </form>
                </div>
            )}
            </div>
        </li>
    );
};

const AuctionMemberPage = () => {
    const { serverId } = useParams();
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [favorites, setFavorites] = useState([]);
    const { monsters } = useZoneMonsters(serverId);
    const now = useServerClock(data?.now);

    const load = useCallback(() => api.get(`/auctions/${serverId}/current`)
        .then(res => { setData(res.data); setFavorites(res.data.favorites || []); setLoadError(false); })
        .catch(() => setLoadError(true)), [serverId]);

    useEffect(() => { load(); }, [load]);

    // Mises des autres : rafraîchissement régulier pendant les enchères
    const running = data?.auction?.running;
    useEffect(() => {
        if (!running) return undefined;
        const id = setInterval(load, 10000);
        return () => clearInterval(id);
    }, [running, load]);

    const placeBid = async (lot, amount) => {
        try {
            const res = await api.post(`/auctions/${serverId}/lots/${lot._id}/bid`, { amount });
            success(res.data.extended
                ? t('auctionMember.bidExtended', 'Mise enregistrée sur « {zone} » · anti-snipe : la zone est prolongée', { zone: lot.zone })
                : t('auctionMember.nowLeading', 'Tu es en tête sur « {zone} »', { zone: lot.zone }));
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionMember.bidError', 'La mise n\'a pas pu être enregistrée'));
        }
        load();
    };

    // Favoris : mise à jour immédiate de l'affichage, puis on reprend la liste renvoyée par le serveur
    const toggleFavorite = async (zone) => {
        const favorite = !favorites.includes(zone);
        setFavorites(prev => (favorite ? [...prev, zone] : prev.filter(z => z !== zone)));
        try {
            const res = await api.put(`/auctions/${serverId}/favorites/${encodeURIComponent(zone)}`, { favorite });
            setFavorites(res.data.favorites || []);
        } catch {
            setFavorites(prev => (favorite ? prev.filter(z => z !== zone) : [...prev, zone]));
            showError(t('auctionMember.favoriteError', 'Le favori n\'a pas pu être enregistré'));
        }
    };

    if (loadError) {
        return <div className="c2-scope c2-admin-page"><p className="auc-error">{t('auctionMember.loadError', 'Impossible de charger les enchères.')}</p></div>;
    }
    if (!data) {
        return <div className="c2-scope c2-admin-page"><div className="c2-loading"><div className="c2-spinner" /></div></div>;
    }

    const { auction, lots } = data;
    const myWins = lots.filter(l => l.isLeader && (l.ended || !running));

    // Pas d'enchère en cours
    if (!auction || !running) {
        const sold = lots.filter(l => l.amount > 0).sort((a, b) => b.amount - a.amount);
        return (
            <div className="c2-scope c2-admin-page">
                <PageHeader icon="hammer" title={t('member.nav.auctions', 'Enchères')} subtitle={t('auctionMember.subtitle', 'Les zones restées libres après la phase d\'échange')} />
                <section className="c2-card c2-section auc-closed">
                    <Icon name="lock" size={32} />
                    <h2>{t('auctionMember.noneRunning', 'Aucune enchère en cours')}</h2>
                    <p className="c2-muted">{t('auctionMember.noneRunningHint', 'Les enchères ouvrent à la fin de la saison, après la phase d\'échange de la fiche de réservation.')}</p>
                </section>
                {auction && (
                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="history" size={20} />{t('auctionMember.lastAuction', 'Dernière enchère')} · {auction.title}</h2>
                            <span className="c2-muted">{fmtDateTime(auction.closedAt || auction.lastEndsAt)}</span>
                        </div>
                        {myWins.length > 0 && (
                            <div className="auc-wins">
                                <Icon name="trophy" size={18} />
                                <span>{t('auctionMember.youWon', 'Tu as remporté :')}</span>
                                <ul className="auc-wins__list">
                                    {myWins.map(l => (
                                        <li key={l._id}>
                                            <MonsterThumb monster={monsters[l.zone]} zone={l.zone} size={28} />
                                            <strong>{l.zone}</strong> {fmtKamas(l.amount)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {sold.length === 0
                            ? <p className="c2-muted">{t('auctionMember.noneSold', 'Aucune zone vendue.')}</p>
                            : <AuctionResults lots={lots} monsters={monsters} />}
                    </section>
                )}
            </div>
        );
    }

    const query = search.trim().toLowerCase();
    const visibleLots = lots
        .filter(l => filter === 'all' || (filter === 'mine' && l.hasBid) || (filter === 'free' && l.amount === 0))
        .filter(l => !query || l.zone.toLowerCase().includes(query) || (l.donjon || '').toLowerCase().includes(query))
        // Zones favorites en premier, l'ordre alphabétique est conservé dans chaque groupe
        .sort((a, b) => Number(favorites.includes(b.zone)) - Number(favorites.includes(a.zone)));
    const leading = lots.filter(l => l.isLeader).length;
    const outbid = lots.filter(l => l.hasBid && !l.isLeader).length;

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="hammer"
                title={t('member.nav.auctions', 'Enchères')}
                subtitle={`${auction.title} · ${t('auctionMember.endsIn', 'fin dans {time}', { time: fmtRemaining(new Date(auction.lastEndsAt).getTime() - now) })}`}
            />

            <div className="auc-rules">
                <span><Icon name="gem" size={15} />{t('auctionMember.ruleStart', 'Départ {amount}', { amount: fmtKamas(auction.startingBid) })}</span>
                <span><Icon name="plus" size={15} />{t('auctionMember.ruleIncrement', 'Surenchère de {amount} minimum', { amount: fmtKamas(auction.minIncrement) })}</span>
                <span><Icon name="clock" size={15} />{t('auctionMember.ruleSnipe', 'Anti-snipe : une mise dans les {n} dernières minutes relance {n} min', { n: auction.antiSnipeMinutes })}</span>
                <span><Icon name="lock" size={15} />{t('auctionMember.ruleNoWithdraw', 'Aucun retrait après une mise')}</span>
            </div>

            {(leading > 0 || outbid > 0) && (
                <div className="auc-mystatus">
                    {leading > 0 && <span className="c2-chip c2-chip--green">{leading > 1 ? t('auctionMember.leadingOnZones', 'En tête sur {n} zones', { n: leading }) : t('auctionMember.leadingOnZone', 'En tête sur {n} zone', { n: leading })}</span>}
                    {outbid > 0 && <span className="c2-chip auc-chip--outbid">{outbid > 1 ? t('auctionMember.outbidOnZones', 'Dépassé sur {n} zones', { n: outbid }) : t('auctionMember.outbidOnZone', 'Dépassé sur {n} zone', { n: outbid })}</span>}
                </div>
            )}

            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="map" size={20} />{t('auctionMember.zonesUp', '{n} zones aux enchères', { n: lots.length })}</h2>
                    <div className="auc-tools">
                        <div className="auc-filters" role="tablist" aria-label={t('auctionMember.filterZones', 'Filtrer les zones')}>
                            {FILTERS.map(f => (
                                <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={filter === f.id ? 'is-active' : undefined} onClick={() => setFilter(f.id)}>
                                    {t(f.labelKey, f.label)}
                                </button>
                            ))}
                        </div>
                        <input type="search" className="c2-input c2-input--sm auc-search" placeholder={t('auctionMember.searchZone', 'Rechercher une zone...')} aria-label={t('auctionMember.searchZoneLabel', 'Rechercher une zone')} value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                </div>
                {visibleLots.length === 0 ? (
                    <p className="c2-muted">{t('auctionMember.noMatch', 'Aucune zone ne correspond.')}</p>
                ) : (
                    <ul className="auc-lots">
                        {visibleLots.map(l => (
                            <LotCard
                                key={l._id}
                                lot={l}
                                auction={auction}
                                now={now}
                                onBid={placeBid}
                                favorite={favorites.includes(l.zone)}
                                onToggleFavorite={toggleFavorite}
                                monster={monsters[l.zone]}
                            />
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
};

export default AuctionMemberPage;
